import React, { useState } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { LocationField } from '../../../api'
import Button from '../../UI/Button/Button'
import Icon from '../../UI/Icon/Icon'

export const AdFormLocationTrigger: React.FC = () => {
  const [showAddForm, setShowAddForm] = useState(false)
  const [newLocation, setNewLocation] = useState({
    latitude: '',
    longitude: '',
    relevantText: '',
  })

  const { control } = useFormContext<{
    passDetails: {
      locationFields: LocationField[]
      relevantDate: string
    }
  }>()

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'passDetails.locationFields',
  })

  // Safety check - ensure form context is initialized
  if (!control) {
    return <div>Loading...</div>
  }

  // Handle null locationFields
  const safeFields = Array.isArray(fields) ? fields : []

  // Force enable the button if fields is not properly initialized
  const shouldShowAddButton = Array.isArray(fields) ? safeFields.length < 100 : true

  // Ensure we have valid data before rendering
  if (!Array.isArray(fields)) {
    console.log('Fields is not an array, showing loading state')
    return (
      <div className="flex flex-col bg-white rounded-md h-fit p-4 gap-3">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold">Location Triggers</h2>
          <Button
            type="primary"
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-2 px-4 py-2 h-auto min-w-[140px]"
          >
            <Icon name="upload" className="w-4 h-4" />
            Add Location
          </Button>
        </div>
        <p className="text-sm text-gray-600">Initializing location data...</p>
      </div>
    )
  }

  const handleAddLocation = () => {
    if (safeFields.length >= 100) {
      alert('Maximum 100 locations allowed')
      return
    }

    if (!newLocation.latitude || !newLocation.longitude) {
      alert('Please enter both latitude and longitude')
      return
    }

    if (!newLocation.relevantText) {
      alert('Please enter relevant text')
      return
    }

    const latitude = parseFloat(newLocation.latitude)
    const longitude = parseFloat(newLocation.longitude)

    if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
      alert('Please enter valid latitude and longitude values')
      return
    }

    if (latitude < -90 || latitude > 90) {
      alert('Latitude must be between -90 and 90')
      return
    }

    if (longitude < -180 || longitude > 180) {
      alert('Longitude must be between -180 and 180')
      return
    }

    const locationField: LocationField = {
      latitude,
      longitude,
      relevantText: newLocation.relevantText,
    }

    append(locationField)
    setNewLocation({ latitude: '', longitude: '', relevantText: '' })
    setShowAddForm(false)
  }

  const handleRemoveLocation = (index: number) => {
    remove(index)
  }

  return (
    <div className="flex flex-col bg-white rounded-md h-fit p-4 gap-3">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Location Triggers</h2>
        {shouldShowAddButton && (
          <Button
            type="primary"
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-2 px-4 py-2 h-auto min-w-[140px]"
          >
            <Icon name="upload" className="w-4 h-4" />
            Add Location
          </Button>
        )}
      </div>

      <p className="text-sm text-gray-600">
        Add up to 100 locations where this ad can be triggered. The 10 closest to each user&apos;s location will be
        displayed on their pass. Each location requires latitude and longitude coordinates.
        {Array.isArray(fields) && safeFields.length > 0 && (
          <span className="block mt-1">{safeFields.length}/100 locations added</span>
        )}
      </p>

      {/* Add Location Form */}
      {showAddForm && shouldShowAddButton && (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <h3 className="font-medium mb-3">Add New Location</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Latitude *</label>
              <input
                type="number"
                step="any"
                placeholder="e.g., 40.7128"
                className="input w-full"
                value={newLocation.latitude}
                onChange={e => setNewLocation({ ...newLocation, latitude: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1">Range: -90 to 90</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Longitude *</label>
              <input
                type="number"
                step="any"
                placeholder="e.g., -74.0060"
                className="input w-full"
                value={newLocation.longitude}
                onChange={e => setNewLocation({ ...newLocation, longitude: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1">Range: -180 to 180</p>
            </div>
          </div>

          {/* Relevant Text for Apple Wallet */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Relevant Text *</label>
            <input
              type="text"
              placeholder="Text for Apple Wallet location triggers"
              className="input w-full"
              value={newLocation.relevantText}
              onChange={e => setNewLocation({ ...newLocation, relevantText: e.target.value })}
              required
            />
            <p className="text-xs text-gray-500 mt-1">This text will be displayed for Apple Wallet location triggers</p>
          </div>

          <div className="flex gap-3 mt-6">
            <Button type="primary" onClick={handleAddLocation} className="px-6 py-2 h-auto">
              Add Location
            </Button>
            <Button type="text" onClick={() => setShowAddForm(false)} className="px-6 py-2 h-auto">
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Location List */}
      {Array.isArray(fields) && safeFields.length > 0 && (
        <div className="mt-4">
          <h3 className="font-medium mb-2">Added Locations</h3>
          <div className="space-y-2">
            {safeFields.map((location, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg"
              >
                <div className="flex-1">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-sm font-medium text-gray-700">Latitude:</span>
                      <span className="ml-1 text-sm">{location.latitude}</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-700">Longitude:</span>
                      <span className="ml-1 text-sm">{location.longitude}</span>
                    </div>
                  </div>
                  <div className="mt-2">
                    <span className="text-sm font-medium text-gray-700">Text:</span>
                    <span className="ml-1 text-sm">{location.relevantText}</span>
                  </div>
                </div>
                <Button type="remove" onClick={() => handleRemoveLocation(index)} className="p-2 h-auto w-auto">
                  <Icon name="cross" className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {Array.isArray(fields) && safeFields.length === 0 && !showAddForm && (
        <div className="text-center py-8 text-gray-500">
          <Icon name="info" className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          <p>No locations added yet. Click &quot;Add Location&quot; to get started.</p>
        </div>
      )}
    </div>
  )
}
